// Nearby hospitals for one scenario, prices AND quality side by side (Trent,
// 2026-09-29: "show a table with the quality metrics there too — CMS stars,
// Google rating, % would recommend, mortality… with all their hospital profile
// pages linked"). Used by the scenario page's "near you" block and the
// /scenarios listing cards.
//
// Prices, U.S. rank and the drive verdict come from useLocalScenarioPrices (the
// same numbers as the rest of the block). The quality columns are the city
// embed's column registry (src/embed/columns.ts) — same labels, formats and
// "which end is better" as the embed and Atlas — read from the Atlas hospital
// pack (globe/hospitals.json, ~0.7 MB gzip), fetched only when a table is
// actually shown; until it lands the scenario manifest's own stars / recommend
// / Google / ER figures fill in.
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { titleCaseCity, titleCaseHospital } from "@/lib/hospitalName";
import { loadHospitalPack, type AtlasHospital } from "@/atlas/data";
import { HOSP_COL_BY_ID, type HospCol } from "@/embed/columns";
import { rankText, type LocalHospital } from "@/hooks/useLocalScenarioPrices";
import type { DriveVerdict } from "@/lib/localSavings";
import { GRADE_COLOR } from "@/embed/parts";

/** The price grade chip (AA: white letter on the embed's deep grade colours). */
export function GradeBadge({ g, title }: { g: string; title?: string }) {
  return (
    <span title={title ?? `Price grade ${g}`} aria-label={`Price grade ${g}`}
      className="inline-grid h-6 w-6 flex-shrink-0 place-items-center rounded-full text-[11px] font-extrabold text-white" style={{ background: GRADE_COLOR[g] ?? "#64748b" }}>{g}</span>
  );
}

/** The quality columns shown, in order. */
const QUALITY = ["cs", "gr", "rp", "mo", "ra", "sr", "ew"] as const;

let packMap: Promise<Map<string, AtlasHospital>> | null = null;
function hospitalQuality(): Promise<Map<string, AtlasHospital>> {
  if (!packMap) packMap = loadHospitalPack().then((p) => new Map((p?.hospitals ?? []).map((h) => [h.id, h]))).catch(() => new Map());
  return packMap;
}

interface Props {
  rows: LocalHospital[];
  nearestId: string | null;
  verdictOf: (l: LocalHospital) => DriveVerdict | null;
  median: number | null;
  dark: boolean;
  /** Rows before "Show all". */
  initial?: number;
  caption: string;
}

type SortKey = "miles" | "price" | (typeof QUALITY)[number];
const fmt = (v: number) => "$" + Math.round(v).toLocaleString("en-US");

export default function NearbyQualityTable({ rows, nearestId, verdictOf, median, dark, initial = 10, caption }: Props) {
  const [quality, setQuality] = useState<Map<string, AtlasHospital> | null>(null);
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: "miles", dir: 1 });
  const [all, setAll] = useState(false);
  useEffect(() => { let dead = false; hospitalQuality().then((m) => { if (!dead) setQuality(m); }); return () => { dead = true; }; }, []);

  const C = dark
    ? { text: "var(--civic-cream)", muted: "rgba(var(--civic-cream-rgb),0.72)", line: "rgba(var(--civic-cream-rgb),0.14)", head: "rgba(var(--civic-cream-rgb),0.06)", good: "#6bc9a0", bad: "#ff7082", accent: "#7da6d4", sticky: "var(--civic-navy-deeper)" }
    : { text: "var(--civic-ink)", muted: "rgba(var(--civic-ink-rgb),0.66)", line: "rgba(var(--civic-ink-rgb),0.12)", head: "rgba(0,40,104,0.04)", good: "#0F6B49", bad: "#9e1b2b", accent: "var(--civic-navy)", sticky: "#fff" };

  // A hospital record for the quality columns: the Atlas pack when loaded, else
  // the scenario manifest's own quality fields.
  const recOf = (l: LocalHospital): AtlasHospital =>
    quality?.get(l.id) ?? ({ id: l.id, n: l.info.n, c: l.info.c, s: l.info.s, la: l.info.la ?? 0, lo: l.info.lo ?? 0, g: null,
      cs: l.info.cs ?? undefined, rp: l.info.rp ?? undefined, gr: l.info.gr ?? undefined, gn: l.info.gn ?? undefined, ew: l.info.ew ?? undefined } as AtlasHospital);

  const cols = QUALITY.map((id) => HOSP_COL_BY_ID.get(id)!).filter(Boolean) as HospCol[];

  const sorted = useMemo(() => {
    const out = [...rows];
    const nullsLast = (a: number | null, b: number | null, dir: number) => (a == null && b == null ? 0 : a == null ? 1 : b == null ? -1 : (a - b) * dir);
    if (sort.key === "miles") return out.sort((a, b) => (a.miles - b.miles) * sort.dir);
    if (sort.key === "price") return out.sort((a, b) => (a.price - b.price) * sort.dir || a.miles - b.miles);
    const col = HOSP_COL_BY_ID.get(sort.key)!;
    return out.sort((a, b) => nullsLast(col.sort(recOf(a)), col.sort(recOf(b)), sort.dir) || a.miles - b.miles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sort, quality]);

  const onSort = (key: SortKey, better?: "low" | "high" | null) => setSort((s) =>
    s.key === key ? { key, dir: (s.dir * -1) as 1 | -1 } : { key, dir: better === "high" ? -1 : 1 });
  const aria = (key: SortKey) => (sort.key === key ? (sort.dir === 1 ? "ascending" : "descending") : undefined);
  const Th = ({ k, label, better, align = "right" }: { k?: SortKey; label: string; better?: "low" | "high" | null; align?: "left" | "right" }) => (
    <th scope="col" aria-sort={k ? aria(k) : undefined}
      className={`whitespace-nowrap px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider ${align === "left" ? "text-left" : "text-right"}`}
      style={{ color: C.muted, borderBottom: `1px solid ${C.line}`, background: C.head }}>
      {k ? (
        <button type="button" onClick={() => onSort(k, better)} className="uppercase tracking-wider hover:underline" style={{ color: "inherit", font: "inherit" }}>
          {label}{sort.key === k ? (sort.dir === 1 ? " ▲" : " ▼") : ""}
        </button>
      ) : label}
    </th>
  );

  const shown = all ? sorted : sorted.slice(0, initial);
  const verdictText = (v: DriveVerdict | null, isNearest: boolean): { text: string; color: string } => {
    if (isNearest) return { text: "Nearest to you", color: C.accent };
    if (!v) return { text: "—", color: C.muted };
    switch (v.kind) {
      case "worth_it": return { text: `Worth the drive · save ${fmt(v.saving)}`, color: C.good };
      case "marginal": return { text: "Cheaper, not by enough", color: C.muted };
      case "not_cheaper": return { text: "Not cheaper", color: C.muted };
      default: return { text: "Can't compare", color: C.muted };
    }
  };

  return (
    <div>
      <div className="overflow-x-auto rounded-lg" style={{ border: `1px solid ${C.line}` }}>
        <table className="w-full text-xs" style={{ color: C.text, borderCollapse: "collapse" }}>
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr>
              <th scope="col" className="sticky left-0 z-[1] px-2 py-1.5 text-left text-[10px] font-bold uppercase tracking-wider"
                style={{ color: C.muted, borderBottom: `1px solid ${C.line}`, background: C.sticky, minWidth: "11rem" }}>Hospital</th>
              <Th label="Grade" align="left" />
              <Th k="miles" label="Miles" />
              <Th k="price" label="Blended price" />
              <Th label="vs U.S. median" />
              <Th label="U.S. rank" />
              {cols.map((c) => <Th key={c.id} k={c.id as SortKey} label={c.en} better={c.better} />)}
              <Th label="vs the nearest" align="left" />
            </tr>
          </thead>
          <tbody>
            {shown.map((l) => {
              const rec = recOf(l);
              const d = median ? l.price - median : null;
              const pct = median && d != null ? Math.round((d / median) * 100) : null;
              const v = verdictText(verdictOf(l), l.id === nearestId);
              return (
                <tr key={l.id} style={{ borderBottom: `1px solid ${C.line}` }}>
                  <th scope="row" className="sticky left-0 z-[1] px-2 py-1.5 text-left font-normal" style={{ background: C.sticky }}>
                    <Link to={`/hospital/${l.id}`} className="font-semibold hover:underline" style={{ color: C.text }}>{titleCaseHospital(l.info.n)}</Link>
                    <span className="block text-[11px]" style={{ color: C.muted }}>{titleCaseCity(l.info.c)}, {l.info.s}</span>
                  </th>
                  <td className="px-2 py-1.5"><GradeBadge g={l.letter} title={`Price grade ${l.letter} for this scenario — ${rankText(l)}`} /></td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{Math.round(l.miles)}</td>
                  <td className="px-2 py-1.5 text-right font-bold tabular-nums">{fmt(l.price)}</td>
                  <td className="whitespace-nowrap px-2 py-1.5 text-right tabular-nums" style={{ color: d == null ? C.muted : d < 0 ? C.good : C.bad }}>
                    {d == null ? "—" : `${d < 0 ? "−" : "+"}${fmt(Math.abs(d))} (${d < 0 ? "−" : "+"}${Math.abs(pct!)}%)`}
                  </td>
                  <td className="whitespace-nowrap px-2 py-1.5 text-right tabular-nums" title={rankText(l)}>#{l.rank.toLocaleString()} <span style={{ color: C.muted }}>/ {l.ranked.toLocaleString()}</span></td>
                  {cols.map((c) => {
                    const s = c.show(rec, "en");
                    return <td key={c.id} className="whitespace-nowrap px-2 py-1.5 text-right tabular-nums">{s ?? <span style={{ color: C.muted }}>—</span>}</td>;
                  })}
                  <td className="whitespace-nowrap px-2 py-1.5" style={{ color: v.color }}>{v.text}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2 text-[11px]" style={{ color: C.muted }}>
        <span>Quality: CMS Care Compare, HCAHPS patient surveys, Google. Click a heading to sort. U.S. rank 1 = cheapest.</span>
        {sorted.length > initial && (
          <button type="button" className="font-semibold underline" style={{ color: C.accent }} onClick={() => setAll((v) => !v)}>
            {all ? "Show fewer" : `Show all ${sorted.length}`}
          </button>
        )}
      </div>
    </div>
  );
}
